import type { FeatureModule, FeatureState } from '../types';

/**
 * Ton stumm + Videos pausieren: schaltet jedes <audio>/<video> stumm und
 * pausiert laufende Videos. Fängt zusätzlich später eingefügte oder neu
 * gestartete Medien ab (MutationObserver + capture-`play`), damit Autoplay
 * nicht zurückkehrt. Beim Deaktivieren wird bewusst NICHT automatisch wieder
 * laut geschaltet (sonst könnte unerwartet Ton losbrechen).
 */
export default function createMuteMediaModule(): FeatureModule {
  let enabled = false;
  let observer: MutationObserver | null = null;
  const STORAGE_KEY = 'accessify-mute-media';

  function isOwnUi(el: Element | null): boolean {
    const root =
      document.getElementById('accessify-root') ||
      document.querySelector('[data-accessify-root="true"]');
    return !!root && !!el && (root === el || root.contains(el));
  }

  function silence(m: HTMLMediaElement) {
    if (isOwnUi(m)) return;
    m.muted = true;
    if (m instanceof HTMLVideoElement && !m.paused) {
      try {
        m.pause();
      } catch {
        /* some players guard pause(); ignore */
      }
    }
  }

  function muteAll() {
    document
      .querySelectorAll<HTMLMediaElement>('audio, video')
      .forEach(silence);
  }

  function onPlay(e: Event) {
    if (!enabled) return;
    const m = e.target as HTMLMediaElement;
    if (m && (m.tagName === 'AUDIO' || m.tagName === 'VIDEO')) silence(m);
  }

  function activate() {
    enabled = true;
    muteAll();
    // Re-mute anything that tries to (re)start.
    document.addEventListener('play', onPlay, true);
    // Catch media inserted after activation (SPAs, lazy embeds).
    observer = new MutationObserver(() => {
      if (enabled) muteAll();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;
    document.removeEventListener('play', onPlay, true);
    observer?.disconnect();
    observer = null;
    localStorage.removeItem(STORAGE_KEY);
    // Intentionally no auto-unmute: flipping every player back on could blast
    // audio the visitor did not ask for. They can unmute individual players.
  }

  return {
    id: 'mute-media',
    name: () => 'Mute media',
    description: 'Mute all audio and video and pause autoplaying videos',
    icon: 'mute',
    category: 'cognitive',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'mute-media', enabled }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) activate();
      else deactivate();
    },
  };
}
